import { DeleteOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Descriptions, Dropdown, Grid, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
import { type CapModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/CapModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions, { type PermissionValues } from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import useDeleteModule from '../useDeleteModule';
import CapModuleMainDetails from './CapModuleMainDetails';
import { useUpdateCapModuleDrawer } from './CapModuleUpdateDrawer';

export type CapModulePageProps = {
    module: CapModuleWithPermissionsSpecsFragment;
};

const CapModuleActionsDropdown = ({
    module,
    permissions,
}: CapModulePageProps & { permissions: Pick<PermissionValues, 'hasDeletePermission' | 'hasUpdatePermission'> }) => {
    const { t } = useTranslation(['capModuleDetails']);
    const deleteModule = useDeleteModule(module.id);

    const updateCapModuleDrawer = useUpdateCapModuleDrawer(module);

    const { hasUpdatePermission, hasDeletePermission } = permissions;

    const items = [
        hasUpdatePermission && {
            label: t('capModuleDetails:mainActionButtons.updateSpecs'),
            key: 'updateSpecs',
            icon: <EditOutlined />,
            onClick: updateCapModuleDrawer.open,
        },
        hasDeletePermission && {
            label: t('capModuleDetails:mainActionButtons.delete'),
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: deleteModule,
            danger: true,
        },
    ];

    if (!items.length) {
        return null;
    }

    return (
        <>
            <Dropdown menu={{ items }}>
                <Button type="primary">
                    <Space>
                        <span>{t('capModuleDetails:actions.label')}</span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {updateCapModuleDrawer.render()}
        </>
    );
};

const CapModulePage = ({ module }: CapModulePageProps) => {
    const { t } = useTranslation(['capModuleDetails']);

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('capModuleDetails:descriptions.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('capModuleDetails:descriptions.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<CapModuleActionsDropdown module={module} permissions={permissions} />}
            footer={[
                <Button form="updateMainDetails" htmlType="submit" type="primary">
                    {t('capModuleDetails:actions.update')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules`)}
            title={screens.md ? t('capModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            <CapModuleMainDetails module={module} />
        </ConsolePageWithHeader>
    );
};

export default CapModulePage;
